import { HierarchyTypeEnum, NewRuleDefinitionResponse } from 'app/graphql/generated/graphqlApolloTypes';

import {
  AggregatePin,
  BulkHierarchyPolygons,
  CollectionFilter,
  ExhaustivePolygonFeatureState,
  FeatureStateIds,
  HierarchyType,
  LassoAbleGeometry,
  LassoPolygon,
  MapHierarchySelectionDetails,
  MapSelectionOptions,
  MapSelectionTarget,
  MapSelectionTool,
  PolygonFeatureState,
  StructuredCloneableFeature,
  TerritoryColors,
  TerritoryGroupForMap,
  WorkerManagedState,
  Segment,
  NamedRootHierarchy
} from 'app/models';

import { calculateSetDifferences, toggleItemsAsSet } from 'utils/helpers/collectionUtils';
import { isEmptyLegacyFilter } from 'utils/helpers/segmentFilterUtils';
import {
  assignFallbackColors,
  attachDecompressedRuleDefinition,
  generateRuleDefinition,
  getLassoBounds,
  isGeometryWithinLasso,
  isSelectionAllowedByOptions
} from 'utils/helpers/territoryMapUtils';
import { RuleUndoStack } from 'utils/RuleUndoStack';

import { AccountLookups } from './AccountLookups';
import { GeoLookups } from './GeoLookups';
import {
  AnalyticsMessage,
  FromMapWorkerMessage,
  MapAnswerOutput,
  MapQuestionAnswerTypeMapping,
  MapQuestionMessage,
  RulesChangeMessage,
  ToMapWorkerMessage
} from './MapWorkerProtocol';
import { RuleLookups } from './RuleLookups';

export const emptyPolygonFeatureState: ExhaustivePolygonFeatureState = {
  ruleId: null,
  territoryId: null,
  territoryName: null,
  ruleColor: null,
  groupColor: null,
  groupName: null,
  groupOwner: null,
  isOverAssignedRule: false,
  isRuleIgnored: false,
  isSelected: false,
  isOverAssignedGeo: false,
  overAssignmentCount: 0
};

export class MapActorV2 {
  private geoLookups = new GeoLookups(null);
  private ruleLookups = RuleLookups.createInitialRuleLookups();
  private accountLookups = new AccountLookups([], this.ruleLookups);

  private readonly colorCache: Map<RuleId, TerritoryColors> = new Map();
  private readonly undoStack = new RuleUndoStack();

  private lockedRuleIds = new Set<RuleId>();
  private ignoredRuleIds = new Set<RuleId>();
  private territoryGroupLookup: ReadonlyMap<RuleId, TerritoryGroupForMap> = new Map();
  private geoFeaturesByRuleId = new Map<RuleId, ReadonlyArray<FeatureStateIds>>();

  private readonly selectedRules = new Set<RuleId>();
  private readonly selectedGeos = new Set<GeoId>();
  private readonly selectedAccounts = new Set<AccountId>();

  private segments = Array<Segment>();

  constructor(private readonly postMessage: (message: FromMapWorkerMessage) => void) {}

  public onMessage(message: ToMapWorkerMessage): void {
    switch (message.type) {
      // Primary datums
      case 'geo-hierarchy-change':
        this.withLoading('isLoadingGeoHierarchy', () => this.onGeoHierarchyChange(message.bulkHierarchyPolygons));
        break;
      case 'rules-change':
        this.withLoading('isLoadingRules', () => this.onRuleChange(message));
        break;
      case 'customer-pins-change':
        this.withLoading('isLoadingCustomerPins', () => this.onCustomerPinsChange(message.customerPins));
        break;
      case 'custom-hierarchy-filter-change':
        this.onCustomHierarchyFilterChange(message.filter);
        break;

      // Locking & Ignoring
      case 'toggle-rules-locked':
        this.onToggleRulesLocked(message.ruleIds);
        break;
      case 'toggle-all-rules-locked':
        this.onToggleRulesLocked([...this.ruleLookups.getAllRuleIds()]);
        break;
      case 'toggle-rules-ignored':
        this.onToggleRulesIgnored(message.ruleIds);
        break;
      case 'toggle-all-rules-ignored':
        this.onToggleRulesIgnored([...this.ruleLookups.getAllRuleIds()]);
        break;
      case 'isolate-rules':
        this.onIsolateRules();
        break;
      case 'un-isolate-rules':
        this.onUnIsolateRules();
        break;

      // Selection (Fast)
      case 'click-polygon':
        this.onClickPolygon(message.polygonId, message.selectionOptions);
        break;
      case 'click-pin':
        this.onClickPin(message.accountId, message.selectionOptions);
        break;

      // Selection (Slow)
      case 'lasso-pins':
        this.withLoading('isLoadingSelection', () => this.onLassoPins(message.accountIds, message.selectionOptions));
        break;
      case 'lasso-polygons':
        this.withLoading('isLoadingSelection', () =>
          this.onLassoPolygons(message.lassoPolygon, message.polygonsInBounds, message.selectionOptions)
        );
        break;
      case 'set-selected-rules':
        this.withLoading('isLoadingSelection', () => this.onSetSelectedRules(message.ruleIds));
        break;
      case 'clear-selection':
        this.onClearSelection();
        break;

      // Misc
      case 'segments-change':
        this.updateSegments(message.segments);
        break;
      case 'group-lookup-change':
        this.onGroupLookupChange(message.groupLookup);
        break;
      case 'get-managed-state':
        this.onRequestFullState();
        break;
      case 'get-initial-selected-accounts':
        this.onGetInitialSelectedAccounts();
        break;
      case 'question':
        this.onQuestion(message);
        break;
      case 'echo':
        this.postMessage(message);
        break;
      default:
        throw new Error(`${(message as ToMapWorkerMessage).type} not yet implemented`);
    }
  }

  public getAnalyticsExtras(): AnalyticsMessage['workerExtras'] {
    return {
      primaryHierarchyType: this.ruleLookups.getPrimaryHierarchyType(),
      customHierarchyQuantity: this.ruleLookups.getCustomHierarchyQuantity(),
      geoCount: this.geoLookups.getGeoCount(),
      accountCount: this.accountLookups.getCount(),
      ruleCount: this.ruleLookups.getRuleCount(),
      undoCount: this.undoStack.getCount(),
      lockedCount: this.lockedRuleIds.size,
      ignoredCount: this.lockedRuleIds.size,
      selectedRulesCount: this.selectedRules.size,
      selectedGeosCount: this.selectedGeos.size,
      selectedAccountsCount: this.selectedAccounts.size,
      isLegacyActor: false
    };
  }

  private onQuestion(message: MapQuestionMessage<keyof MapQuestionAnswerTypeMapping>) {
    const baseAnswer = {
      type: 'answer' as const,
      questionId: message.questionId,
      questionKind: message.questionKind,
      input: message.input,
      output: null,
      error: null
    };
    try {
      this.postMessage({
        ...baseAnswer,
        output: this.generateAnswerOutput(message)
      });
    } catch (error) {
      console.debug('Error while answering question', error);
      this.postMessage({
        ...baseAnswer,
        error: getErrorMessage(error)
      });
    }
  }

  private generateAnswerOutput<T extends keyof MapQuestionAnswerTypeMapping>({
    questionKind,
    input
  }: MapQuestionMessage<T>): MapAnswerOutput<T> {
    switch (questionKind) {
      case 'get-fix-conflict-details': {
        return this.answerGetFixConfictDetails();
      }
      case 'get-names-for-unassign': {
        return this.answerGetNamesForUnassign();
      }
      case 'initiate-undo': {
        return this.answerInitiateUndo();
      }
      case 'get-details-for-create-rule': {
        return this.answerGetDetailsForCreateRule();
      }
      case 'get-territory-counts-for-segments': {
        return this.answerGetTerritoryCountsForSegments();
      }
      case 'get-custom-hierarchies-in-territory-rule': {
        return this.answerGetCustomHierarchiesInTerritoryRule(input);
      }

      default:
        throw new Error(`Unknown question kind ${questionKind}`);
    }
  }
  private answerGetTerritoryCountsForSegments(): MapAnswerOutput<'get-territory-counts-for-segments'> {
    return this.ruleLookups.getTerritoryCountsForSegments(this.segments);
  }

  private answerGetCustomHierarchiesInTerritoryRule(
    input
  ): MapAnswerOutput<'get-custom-hierarchies-in-territory-rule'> {
    return this.ruleLookups.getCustomHierarchiesInRule(input);
  }

  private answerGetDetailsForCreateRule(): MapAnswerOutput<'get-details-for-create-rule'> {
    const selectedCustomHierarchies = this.ruleLookups.getNamedHierarchiesFromFilter();
    const customHierarchyIds = selectedCustomHierarchies.map(({ customHierarchyId }) => customHierarchyId);

    let definition: NewRuleDefinitionResponse;
    switch (this.ruleLookups.getPrimaryHierarchyType()) {
      case HierarchyType.GeographicTerritoryHierarchy:
        definition = generateRuleDefinition({
          accountOverrideIds: [...this.selectedAccounts],
          geoIds: [...this.selectedGeos],
          customHierarchyIds
        });
        break;
      case HierarchyType.CustomerAccountHierarchy:
        definition = generateRuleDefinition({
          accountIds: [...this.selectedAccounts],
          customHierarchyIds
        });
        break;
      default:
        throw new Error(`Unexpected primary hierarchy type`);
    }

    return {
      definition,
      selectedCustomHierarchies
    };
  }

  private answerInitiateUndo(): MapAnswerOutput<'initiate-undo'> {
    return { rules: this.undoStack.pop().map(({ rule, definition }) => ({ ...rule, definition })) };
  }

  private answerGetNamesForUnassign(): MapAnswerOutput<'get-names-for-unassign'> {
    if (this.selectedGeos.size === 0 && this.selectedAccounts.size === 0) throw new Error('No selected hierarchies');

    let hierarchiesBeingRemovedCount = 0;
    const ruleNames = new Set<string>();

    for (const geoId of this.selectedGeos) {
      const ruleId = this.ruleLookups.getGeoAssignment(geoId);
      if (!ruleId) continue;
      const rule = this.ruleLookups.getRule(ruleId);
      if (!rule) continue;
      ruleNames.add(rule.territoryName);
      hierarchiesBeingRemovedCount++;
    }

    const onlyUnassignModifierAccounts =
      this.ruleLookups.getPrimaryHierarchyType() === HierarchyType.GeographicTerritoryHierarchy;
    for (const accountId of this.selectedAccounts) {
      const account = this.accountLookups.getAccount(accountId);
      if (!account?.ruleId) continue;
      if (onlyUnassignModifierAccounts && !account.isModifierAccount) continue;
      const rule = this.ruleLookups.getRule(account.ruleId);
      ruleNames.add(rule.territoryName);
      hierarchiesBeingRemovedCount++;
    }

    return {
      ruleNames,
      hierarchiesBeingRemovedCount
    };
  }

  private answerGetFixConfictDetails(): MapAnswerOutput<'get-fix-conflict-details'> {
    if (this.selectedRules.size !== 1) throw new Error(`Must only remove conflict from single rule`);

    const [selectedRuleId] = this.selectedRules;
    const selectedRule = this.ruleLookups.getRule(selectedRuleId);
    if (!selectedRule.isOverAssigned) throw new Error(`Rule ID ${selectedRuleId} has no conflict`);

    let hierarchyIdsBeingRemoved: Array<GeoId | AccountId>;
    let hierarchyNamesBeingRemoved: Array<string>;

    switch (this.ruleLookups.getPrimaryHierarchyType()) {
      case HierarchyType.GeographicTerritoryHierarchy:
        hierarchyIdsBeingRemoved = this.ruleLookups.getOverAssignedGeosForRule(selectedRuleId);
        hierarchyNamesBeingRemoved = hierarchyIdsBeingRemoved.map((geoId) => this.geoLookups.getGeoName(geoId));
        break;
      case HierarchyType.CustomerAccountHierarchy:
        hierarchyIdsBeingRemoved = this.ruleLookups.getOverAssignedAccountsForRule(selectedRuleId);
        hierarchyNamesBeingRemoved = hierarchyIdsBeingRemoved.map(
          (accountId) => this.accountLookups.getAccount(accountId)?.accountName ?? ''
        );
        break;
      default:
        throw new Error(`Unexpected primary hierarchy type`);
    }

    return {
      hierarchyIdsBeingRemoved,
      hierarchyNamesBeingRemoved,
      territoryName: selectedRule.territoryName,
      territoryId: selectedRule.territoryId,
      ruleId: selectedRule.ruleId
    };
  }

  private onToggleRulesIgnored(ruleIdsToToggle: RuleId[]) {
    this.handleIgnoredRulesChange(
      toggleItemsAsSet(this.ignoredRuleIds, ruleIdsToToggle.filter(this.isLockAndIgnoreToggleable))
    );

    this.computeIsolateEnabled();
  }

  private updateSegments(segments: Array<Segment>): void {
    this.segments = segments;

    this.updateRemoteManagedState({
      segments,
      customHierarchies: this.getAvailableCustomHierarchies()
    });
  }

  private handleIgnoredRulesChange(nextIgnoredRuleIds: Set<RuleId>) {
    this.removeRelatedSelections(nextIgnoredRuleIds);

    const ignoredRuleDelta = calculateSetDifferences(this.ignoredRuleIds, nextIgnoredRuleIds);
    this.setIgnoredFeatureState(ignoredRuleDelta.removed, false);
    this.setIgnoredFeatureState(ignoredRuleDelta.added, true);

    this.ignoredRuleIds = nextIgnoredRuleIds;
    this.updateRemoteManagedState({ ignoredRuleIds: this.ignoredRuleIds });
  }

  private onToggleRulesLocked(ruleIdsToToggle: RuleId[]) {
    this.handleLockedRulesChange(
      toggleItemsAsSet(this.lockedRuleIds, ruleIdsToToggle.filter(this.isLockAndIgnoreToggleable))
    );
  }

  private handleLockedRulesChange(nextLockedRuleIds: Set<RuleId>) {
    this.removeRelatedSelections(nextLockedRuleIds);

    this.lockedRuleIds = nextLockedRuleIds;
    this.updateRemoteManagedState({ lockedRuleIds: this.lockedRuleIds });
  }

  private onUnIsolateRules() {
    this.onToggleRulesIgnored([...this.ignoredRuleIds]);
    this.computeIsolateEnabled();
  }

  private onIsolateRules() {
    let rulesInvolvedInSelection: Set<number>;
    if (this.selectedRules.size > 0) rulesInvolvedInSelection = new Set(this.selectedRules);
    if (this.selectedGeos.size > 0) rulesInvolvedInSelection = this.getRuleIdsByGeos(this.selectedGeos);
    if (this.selectedAccounts.size > 0) rulesInvolvedInSelection = this.getRuleIdsByAccounts(this.selectedAccounts);
    if (!rulesInvolvedInSelection?.size) return;

    const rulesToIgnore = new Set(this.ignoredRuleIds);
    for (const ruleId of this.ruleLookups.getAllRuleIds()) {
      if (rulesInvolvedInSelection.has(ruleId)) continue;
      if (!this.ruleLookups.getRule(ruleId)?.isPassingFilter) continue;
      rulesToIgnore.add(ruleId);
    }
    this.handleIgnoredRulesChange(rulesToIgnore);

    this.computeIsolateEnabled();
  }

  private computeIsolateEnabled() {
    if (this.ignoredRuleIds.size === 0)
      return this.updateRemoteManagedState({ isAllIsolatedTerritoriesSelected: false });

    const visibleRules = new Set(
      [...this.ruleLookups.getAllRuleIds()].filter((ruleId) => {
        const rule = this.ruleLookups.getRule(ruleId);
        return !this.ignoredRuleIds.has(ruleId) && rule.isMappable && this.isLockAndIgnoreToggleable(ruleId);
      })
    );

    let selectedRuleIds: Set<number>;

    if (this.selectedGeos.size > 0) {
      selectedRuleIds = this.getRuleIdsByGeos(this.selectedGeos);
    } else if (this.selectedAccounts.size > 0) {
      selectedRuleIds = this.getRuleIdsByAccounts(this.selectedAccounts);
    } else {
      selectedRuleIds = this.selectedRules;
    }

    const isAllNonIgnoredRulesSelected =
      selectedRuleIds.size > 0 &&
      selectedRuleIds.size === visibleRules.size &&
      [...selectedRuleIds].every((value) => visibleRules.has(value));

    this.updateRemoteManagedState({ isAllIsolatedTerritoriesSelected: isAllNonIgnoredRulesSelected });
  }

  // to do: use this method
  private getRuleIdsByGeos(geoIds: Set<GeoId>): Set<RuleId> {
    const ruleIds = new Set<RuleId>();
    for (const geoId of geoIds) {
      const ruleId = this.ruleLookups.getGeoAssignment(geoId);
      if (ruleId) {
        ruleIds.add(ruleId);
      }
    }
    return ruleIds;
  }

  private getRuleIdsByAccounts(accounts: Iterable<AccountId>): Set<RuleId> {
    const ruleIds = new Set<RuleId>();
    for (const accountId of accounts) {
      const ruleId = this.accountLookups.getAccount(accountId)?.ruleId;
      if (ruleId) {
        ruleIds.add(ruleId);
      }
    }
    return ruleIds;
  }

  private removeRelatedSelections(ruleIds: ReadonlySet<RuleId>) {
    if (this.selectedRules.size > 0) {
      this.alterRuleSelection(ruleIds, false);
    }
    if (this.selectedGeos.size > 0) {
      const geoIdsToRemove = new Array<GeoId>();
      for (const geoId of this.selectedGeos) {
        const assignmentId = this.ruleLookups.getGeoAssignment(geoId);
        if (assignmentId && ruleIds.has(assignmentId)) {
          geoIdsToRemove.push(geoId);
        }
      }
      this.alterGeoSelection(geoIdsToRemove, false);
    }
    if (this.selectedAccounts.size > 0) {
      const accountIdsToRemove = new Array<AccountId>();
      for (const accountId of this.selectedAccounts) {
        const assignmentId = this.accountLookups.getAccount(accountId).ruleId;
        if (assignmentId && ruleIds.has(assignmentId)) {
          accountIdsToRemove.push(accountId);
        }
      }
      this.alterAccountSelection(accountIdsToRemove, false);
    }
  }

  private setIgnoredFeatureState(ruleIds: Iterable<RuleId>, isIgnored: boolean) {
    const geoFeatureIds = new Array<FeatureStateIds>();
    for (const ruleId of ruleIds) {
      const features = this.geoFeaturesByRuleId.get(ruleId);
      if (features) geoFeatureIds.push(...features);
    }
    if (geoFeatureIds.length === 0) return;
    this.postMessage({
      type: 'set-feature-state',
      bulk: { ids: geoFeatureIds, state: { isRuleIgnored: isIgnored } }
    });
  }

  private isLockAndIgnoreToggleable = (ruleId: RuleId) => {
    return !!this.ruleLookups.getRule(ruleId)?.isPassingFilter;
  };

  private onSetSelectedRules(ruleIds: readonly number[]): void {
    if (this.selectedGeos.size > 0) this.clearSelection();
    if (this.selectedAccounts.size > 0) this.clearSelection();
    const changes = calculateSetDifferences(this.selectedRules, new Set(ruleIds));
    if (changes.added.size > 0) this.alterRuleSelection(changes.added, true);
    if (changes.removed.size > 0) this.alterRuleSelection(changes.removed, false);
  }

  private onLassoPins(accountIds: AccountId[], options: MapSelectionOptions): void {
    if (options.tool === MapSelectionTool.togglePointer) return;
    const selectableAccountIds = accountIds.filter((accountId) => {
      const account = this.accountLookups.getAccount(accountId);
      return !account.accountOverassignmentCount && this.isSelectionAllowedForAssignment(options, account.ruleId);
    });

    const isAddingToSelection = options.tool === MapSelectionTool.addLasso;
    switch (options.target) {
      case MapSelectionTarget.accounts:
        this.alterAccountSelection(selectableAccountIds, isAddingToSelection);
        break;
      case MapSelectionTarget.territories:
        this.alterRuleSelection(this.getRuleIdsByAccounts(selectableAccountIds), isAddingToSelection);
        break;
      default:
        throw new Error(`Unexpected selection target ${options.target}`);
    }
  }

  private onClickPin(accountId: AccountId, options: MapSelectionOptions) {
    if (options.tool !== MapSelectionTool.togglePointer) return;

    const { ruleId, accountOverassignmentCount } = this.accountLookups.getAccount(accountId);
    if (accountOverassignmentCount) return;
    if (!this.isSelectionAllowedForAssignment(options, ruleId)) return;

    switch (options.target) {
      case MapSelectionTarget.accounts:
        this.alterAccountSelection([accountId], !this.selectedAccounts.has(accountId));
        break;
      case MapSelectionTarget.territories:
        this.alterRuleSelection([ruleId], !this.selectedRules.has(ruleId));
        break;
      default:
        throw new Error(`Unexpected selection target ${options.target}`);
    }
  }

  private onClickPolygon(polygonId: string, selectionOptions: MapSelectionOptions) {
    if (selectionOptions.tool !== MapSelectionTool.togglePointer) return;

    const polygon = this.geoLookups.getPolygonById(polygonId);
    if (!polygon || this.ruleLookups.isGeoOverassigned(polygon.geoId)) return;

    const ruleId = this.ruleLookups.getGeoAssignment(polygon.geoId);
    if (!this.isSelectionAllowedForAssignment(selectionOptions, ruleId)) return;

    switch (selectionOptions.target) {
      case MapSelectionTarget.polygons: {
        const isAddingToSelection = !this.selectedGeos.has(polygon.geoId);
        this.alterGeoSelection([polygon.geoId], isAddingToSelection);
        break;
      }
      case MapSelectionTarget.territories: {
        const isAddingToSelection = !this.selectedRules.has(ruleId);
        this.alterRuleSelection([ruleId], isAddingToSelection);
        break;
      }
      default:
        throw new Error(`Unexpected selection target ${selectionOptions.target}`);
    }
  }

  private onLassoPolygons(
    lassoPolygon: LassoPolygon,
    polygonsInBounds: ReadonlyArray<StructuredCloneableFeature>,
    options: MapSelectionOptions
  ): void {
    if (this.ruleLookups.getPrimaryHierarchyType() !== HierarchyType.GeographicTerritoryHierarchy) return;
    if (options.tool === MapSelectionTool.togglePointer) return;

    type SelectableItemId = GeoId | RuleId;
    const itemsToToggle = new Set<SelectableItemId>();
    let itemsInSelection: ReadonlySet<SelectableItemId>;
    switch (options.target) {
      case MapSelectionTarget.polygons:
        itemsInSelection = this.selectedGeos;
        break;
      case MapSelectionTarget.territories:
        itemsInSelection = this.selectedRules;
        break;
      default:
        throw new Error(`Unexpected selection target ${options.target}`);
    }

    const isAddingToSelection = options.tool === MapSelectionTool.addLasso;
    const lassoGeometry = (polygonId: string | undefined, geometry: LassoAbleGeometry) => {
      const polygon = this.geoLookups.getPolygonById(polygonId);
      if (!polygon || this.ruleLookups.isGeoOverassigned(polygon.geoId)) return;

      const ruleId = this.ruleLookups.getGeoAssignment(polygon.geoId);
      if (!this.isSelectionAllowedForAssignment(options, ruleId)) return;
      const itemId = options.target === MapSelectionTarget.territories ? ruleId : polygon.geoId;

      if (itemsToToggle.has(itemId)) return;
      if (itemsInSelection.has(itemId) === isAddingToSelection) return;
      if (!isGeometryWithinLasso(geometry, lassoPolygon)) return;

      itemsToToggle.add(itemId);
    };

    for (const index of this.geoLookups.getSpatialIndexes()) {
      index.forEachInBounds(getLassoBounds(lassoPolygon), ({ lat, lon, value: polygonId }) =>
        lassoGeometry(polygonId, { type: 'Point', coordinates: [lon, lat] })
      );
    }

    polygonsInBounds.forEach((mapPolygon) => {
      lassoGeometry(mapPolygon.id?.toString(), mapPolygon.geometry);
    });

    if (itemsToToggle.size === 0) return;

    switch (options.target) {
      case MapSelectionTarget.polygons:
        this.alterGeoSelection(itemsToToggle, isAddingToSelection);
        break;
      case MapSelectionTarget.territories:
        this.alterRuleSelection(itemsToToggle, isAddingToSelection);
        break;
    }
  }

  private notifyOnEmptyRules(namesOfEmptyRules: string[]) {
    if (namesOfEmptyRules.length > 0) {
      this.postMessage({
        type: 'selection-notification',
        notificationKind: 'empty-rule',
        details: { territoryNames: namesOfEmptyRules }
      });
    }
  }

  private hasSelection() {
    if (this.selectedRules.size > 0) return true;
    if (this.selectedGeos.size > 0) return true;
    if (this.selectedAccounts.size > 0) return true;
    return false;
  }

  private onClearSelection() {
    if (this.hasSelection()) this.withLoading('isLoadingSelection', () => this.clearSelection());
  }

  private onRequestFullState() {
    const state: WorkerManagedState = {
      // Selection
      selectedGeoIds: [...this.selectedGeos],
      selectedRuleIds: [...this.selectedRules],
      selectedAccounts: [...this.selectedAccounts],
      hierarchySelectionDetails: this.getGeoHieararchySelectionDetails() ?? this.getAccountHierarchySelectionDetails(),

      // Geo info
      sourceGroups: this.geoLookups.getSourceGroups(),
      polygonNameMap: this.geoLookups.getPolygonNameMap(),
      geographyNameMap: this.geoLookups.getGeographyNameMap(),
      bounds: this.geoLookups.getBounds(),

      // Rule info
      rules: [...this.ruleLookups.getAllRules()],
      customHierarchies: this.getAvailableCustomHierarchies(),
      customHierarchyFilter: this.ruleLookups.getFilter(),

      // Rule related
      isUndoAvailable: this.undoStack.isUndoAvailable(),
      lockedRuleIds: this.lockedRuleIds,
      ignoredRuleIds: this.ignoredRuleIds,

      // Account info
      customers: this.accountLookups.getAllAccounts(),

      // Loading status
      isLoadingGeoHierarchy: false,
      isLoadingSelection: false,
      isLoadingRules: false,
      isLoadingCustomerPins: false,

      isAllIsolatedTerritoriesSelected: false,

      // Segments
      segments: this.segments
    };
    this.updateRemoteManagedState(state);
  }

  private getAvailableCustomHierarchies() {
    const allHierarchies = new Map([
      ...this.ruleLookups.getAllCustomHierarchiesInRules(),
      ...this.extractNamedHierarchiesFromSegments(this.segments)
    ]);

    return [...allHierarchies.values()].sort(
      (a, b) => a.customHierarchyName.localeCompare(b.customHierarchyName) || a.customHierarchyId - b.customHierarchyId
    );
  }

  private extractNamedHierarchiesFromSegments(segments: Iterable<Segment>) {
    const allHierarchies = new Map<number, NamedRootHierarchy>();
    for (const segment of segments) {
      // Currently only single-clause segments are supported
      if (segment.clauses.length !== 1) continue;

      for (const clause of segment.clauses) {
        if (clause.hierarchyType !== HierarchyTypeEnum.CustomHierarchy) continue;
        if (clause.rootHierarchyId !== this.ruleLookups.getCustomRootHierarchyId()) continue;
        for (const hierarchy of clause.hierarchies) {
          allHierarchies.set(hierarchy.hierarchyId, {
            customHierarchyId: hierarchy.hierarchyId,
            customHierarchyName: hierarchy.hierarchyName,
            rootHierarchyId: clause.rootHierarchyId,
            rootHierarchyName: clause.rootHierarchyName
          });
        }
      }
    }
    return allHierarchies;
  }

  private onGetInitialSelectedAccounts() {
    if (this.selectedAccounts.size > 0) {
      this.updateAccountSelectionFeatureState([...this.selectedAccounts], true);
    } else if (this.selectedRules.size > 0) {
      const accountIds = new Array<AccountId>();
      for (const ruleId of this.selectedRules) {
        accountIds.push(...(this.accountLookups.getFeaturesByRuleId(ruleId) ?? []));
      }
      this.updateAccountSelectionFeatureState(accountIds, true);
    }
  }

  private onCustomHierarchyFilterChange(nextFilter: CollectionFilter<AccountId>[]) {
    const currentFilter = this.ruleLookups.getFilter();
    if (currentFilter.length === 0 && nextFilter.length === 0) return;
    const canSkipEvaluatingNextFilter = isEmptyLegacyFilter(nextFilter) && isEmptyLegacyFilter(currentFilter);
    if (canSkipEvaluatingNextFilter) {
      this.ruleLookups.mutateFilterKind(nextFilter);
      this.updateRemoteManagedState({ customHierarchyFilter: this.ruleLookups.getFilter() });
      return;
    }
    this.clearSelection();
    this.parseAndApplyRules(this.ruleLookups.createNextRuleLookupsForFilterChange(nextFilter));
  }

  private onCustomerPinsChange(rawAccounts: ReadonlyArray<AggregatePin>): void {
    this.accountLookups = new AccountLookups(rawAccounts, this.ruleLookups);
    this.updateRemoteManagedState({ customers: this.accountLookups.getAllAccounts() });
  }

  private recalcualtePinsAfterRuleChange() {
    this.accountLookups = new AccountLookups(this.accountLookups.getRawAccounts(), this.ruleLookups);
    this.updateRemoteManagedState({ customers: this.accountLookups.getAllAccounts() });
  }

  private onGeoHierarchyChange(geoHierarchy: BulkHierarchyPolygons): void {
    if (this.ruleLookups.hasRules()) {
      const originalRuleLookups = this.ruleLookups;
      // Clear out rules to prevent assigned polygons being orphaned
      this.parseAndApplyRules(RuleLookups.createInitialRuleLookups());
      this.geoLookups = new GeoLookups(geoHierarchy);
      this.parseAndApplyRules(originalRuleLookups);
    } else {
      this.geoLookups = new GeoLookups(geoHierarchy);
    }
    this.updateRemoteManagedState({
      polygonNameMap: this.geoLookups.getPolygonNameMap(),
      geographyNameMap: this.geoLookups.getGeographyNameMap(),
      sourceGroups: this.geoLookups.getSourceGroups(),
      bounds: this.geoLookups.getBounds()
    });
  }

  private onRuleChange(message: RulesChangeMessage): void {
    assignFallbackColors(message.rules, this.colorCache);
    const rulesAndSettings = {
      primaryHierarchy: message.primaryHierarchy,
      chosenCustomHierarchy: message.chosenCustomHierarchy,
      rules: message.rules.map((rule) => attachDecompressedRuleDefinition(rule)),
      additionalCustomHierarchies: this.extractNamedHierarchiesFromSegments(this.segments)
    };
    if (message.isUndoEnabled) {
      this.undoStack.onRuleChange(message.cause, rulesAndSettings.rules);
    } else {
      this.undoStack.resetStack([]);
    }
    this.clearSelection();
    const allRuleIds = new Set(rulesAndSettings.rules.map((rule) => rule.rule.ruleId));
    this.removeIrrelevantLocksAndIgnores(allRuleIds);
    this.parseAndApplyRules(this.ruleLookups.createNextRuleLookupsForRuleChange(rulesAndSettings));
  }

  private clearSelection() {
    if (this.selectedAccounts.size > 0) this.alterAccountSelection(this.selectedAccounts, false);
    if (this.selectedGeos.size > 0) this.alterGeoSelection(this.selectedGeos, false);
    if (this.selectedRules.size > 0) this.alterRuleSelection(this.selectedRules, false);
  }

  private parseAndApplyRules(nextRuleLookups: RuleLookups): void {
    const { newlyUnassignedGeos } = this.alterRuleLookups(nextRuleLookups);

    this.setFeatureStateForRules();
    this.clearTerritoryAndTerGroupFeatureState(newlyUnassignedGeos);
    this.setFeatureStateForOverassignedGeos();

    this.updateRemoteManagedState({
      rules: [...this.ruleLookups.getAllRules()],
      isUndoAvailable: this.undoStack.isUndoAvailable(),
      customHierarchyFilter: this.ruleLookups.getFilter(),
      customHierarchies: this.getAvailableCustomHierarchies()
    });
    this.recalcualtePinsAfterRuleChange();
  }

  private setFeatureStateForRules() {
    for (const rule of this.ruleLookups.getAllRules()) {
      this.postMessage({
        type: 'set-feature-state',
        bulk: {
          ids: this.geoFeaturesByRuleId.get(rule.ruleId) ?? [],
          state: {
            ...emptyPolygonFeatureState,
            ...this.getTerritoryGroupFeatureState(rule.territoryGroupId),
            isSelected: this.selectedRules.has(rule.ruleId),
            ruleColor: rule.color,
            ruleId: rule.ruleId,
            territoryName: rule.territoryName,
            territoryId: rule.territoryId,
            isOverAssignedRule: rule.isOverAssigned,
            isOverAssignedGeo: false,
            isRuleIgnored: this.ignoredRuleIds.has(rule.ruleId)
          }
        }
      });
    }
  }

  private clearTerritoryAndTerGroupFeatureState(geoIds: Iterable<GeoId>) {
    const featureIds: FeatureStateIds[] = [];
    for (const geoId of geoIds) {
      const polygon = this.geoLookups.getPolygonByGeoId(geoId);
      if (polygon) featureIds.push(polygon.featureStateIds);
    }
    this.postMessage({
      type: 'set-feature-state',
      bulk: {
        ids: featureIds,
        state: emptyPolygonFeatureState
      }
    });
  }

  private setFeatureStateForOverassignedGeos() {
    const featureIdsByCount = new Map<number, FeatureStateIds[]>();
    for (const [geoId, count] of this.ruleLookups.getAllOverAssignedGeoCounts()) {
      const polygon = this.geoLookups.getPolygonByGeoId(geoId);
      if (!polygon) continue;
      if (featureIdsByCount.has(count)) featureIdsByCount.get(count).push(polygon.featureStateIds);
      else featureIdsByCount.set(count, [polygon.featureStateIds]);
    }
    for (const [count, ids] of featureIdsByCount) {
      this.postMessage({
        type: 'set-feature-state',
        bulk: {
          ids,
          state: {
            ...emptyPolygonFeatureState,
            isOverAssignedGeo: true,
            isOverAssignedRule: true,
            overAssignmentCount: count
          }
        }
      });
    }
  }

  private alterRuleLookups(nextRuleLookups: RuleLookups) {
    const newlyUnassignedGeos = new Set(this.ruleLookups.getAllAssignedGeos());

    this.ruleLookups = nextRuleLookups;
    this.geoFeaturesByRuleId.clear();

    for (const rule of this.ruleLookups.getAllRules()) {
      if (!rule.isMappable) continue;
      if (!rule.isPassingFilter) continue;

      const geoFeatures = new Array<FeatureStateIds>();
      for (const geoId of this.ruleLookups.getAllGeosReferencedInRule(rule.ruleId)) {
        if (this.ruleLookups.isGeoOverassigned(geoId)) continue;

        newlyUnassignedGeos.delete(geoId);
        const geoPolygon = this.geoLookups.getPolygonByGeoId(geoId);
        if (geoPolygon) geoFeatures.push(geoPolygon.featureStateIds);
      }
      this.geoFeaturesByRuleId.set(rule.ruleId, geoFeatures);
    }
    return { newlyUnassignedGeos };
  }

  private onGroupLookupChange(groupLookup: ReadonlyMap<RuleId, TerritoryGroupForMap>) {
    this.territoryGroupLookup = groupLookup;
    for (const rule of this.ruleLookups.getAllRules()) {
      this.postMessage({
        type: 'set-feature-state',
        bulk: {
          ids: this.geoFeaturesByRuleId.get(rule.ruleId) ?? [],
          state: this.getTerritoryGroupFeatureState(rule.territoryGroupId)
        }
      });
    }
  }

  private getTerritoryGroupFeatureState(
    territoryGroupId: number
  ): Pick<PolygonFeatureState, 'groupColor' | 'groupName' | 'groupOwner'> {
    const { color, name, owner } = this.territoryGroupLookup.get(territoryGroupId) ?? {};
    const hasOwner = owner && (owner.firstName || owner.lastName);
    return {
      groupColor: color ?? null,
      groupName: name ?? null,
      groupOwner: hasOwner ? `${owner.firstName} ${owner.lastName}` : null
    };
  }

  private alterRuleSelection(ruleIds: Iterable<RuleId>, shouldBeSelected: boolean) {
    const namesOfEmptyRules = new Array<string>();

    const geoFeatureIds: FeatureStateIds[] = [];
    const accountIds = new Array<number>();
    for (const ruleId of ruleIds) {
      if (this.selectedRules.has(ruleId) === shouldBeSelected) continue;
      const rule = this.ruleLookups.getRule(ruleId);
      if (!rule) continue;
      if (shouldBeSelected && (!rule.isMappable || this.isLockedOrIgnored(ruleId))) continue;

      if (shouldBeSelected) this.selectedRules.add(ruleId);
      else this.selectedRules.delete(ruleId);

      geoFeatureIds.push(...(this.geoFeaturesByRuleId.get(rule.ruleId) ?? []));
      accountIds.push(...(this.accountLookups.getFeaturesByRuleId(rule.ruleId) ?? []));
      if (rule.isEmpty) namesOfEmptyRules.push(rule.territoryName);
    }

    this.updateGeoSelectionFeatureState(geoFeatureIds, shouldBeSelected);
    this.updateAccountSelectionFeatureState(accountIds, shouldBeSelected);
    this.updateRemoteManagedState({ selectedRuleIds: [...this.selectedRules] });
    if (shouldBeSelected) this.notifyOnEmptyRules(namesOfEmptyRules);

    this.computeIsolateEnabled();
  }

  private alterGeoSelection(geoIds: Iterable<GeoId>, shouldBeSelected: boolean) {
    const featureIds: FeatureStateIds[] = [];

    for (const geoId of geoIds) {
      if (this.selectedGeos.has(geoId) === shouldBeSelected) continue;
      const polygon = this.geoLookups.getPolygonByGeoId(geoId);
      if (!polygon) continue;
      if (shouldBeSelected && this.ruleLookups.isGeoOverassigned(geoId)) continue;

      if (shouldBeSelected) this.selectedGeos.add(geoId);
      else this.selectedGeos.delete(geoId);

      featureIds.push(polygon.featureStateIds);
    }

    this.updateGeoSelectionFeatureState(featureIds, shouldBeSelected);
    this.updateRemoteManagedState({
      selectedGeoIds: [...this.selectedGeos],
      hierarchySelectionDetails: this.getGeoHieararchySelectionDetails()
    });

    this.computeIsolateEnabled();
  }

  private alterAccountSelection(accountIdsToToggle: Iterable<AccountId>, shouldBeSelected: boolean) {
    const changedIds = new Array<number>();
    for (const accountId of accountIdsToToggle) {
      if (this.selectedAccounts.has(accountId) === shouldBeSelected) continue;
      changedIds.push(accountId);
      if (shouldBeSelected) this.selectedAccounts.add(accountId);
      else this.selectedAccounts.delete(accountId);
    }

    this.updateAccountSelectionFeatureState(changedIds, shouldBeSelected);
    this.updateRemoteManagedState({
      selectedAccounts: [...this.selectedAccounts],
      hierarchySelectionDetails: this.getAccountHierarchySelectionDetails()
    });

    this.computeIsolateEnabled();
  }

  private getGeoHieararchySelectionDetails(): MapHierarchySelectionDetails {
    if (this.selectedGeos.size === 0) return null;

    for (const geoId of this.selectedGeos) {
      if (this.ruleLookups.isGeoAssigned(geoId)) {
        return {
          isOnlyUnassigned: false,
          atLeastOneModifier: null
        };
      }
    }
    return {
      isOnlyUnassigned: true,
      atLeastOneModifier: null
    };
  }

  private getAccountHierarchySelectionDetails(): MapHierarchySelectionDetails {
    if (!this.selectedAccounts.size) return null;
    let isOnlyUnassigned = true;
    let atLeastOneModifier = false;

    for (const accountId of this.selectedAccounts) {
      const account = this.accountLookups.getAccount(accountId);
      isOnlyUnassigned = isOnlyUnassigned && !account.ruleId;
      atLeastOneModifier = atLeastOneModifier || account.isModifierAccount;
      // Can finish early
      if (!isOnlyUnassigned && atLeastOneModifier) break;
    }

    return {
      isOnlyUnassigned,
      atLeastOneModifier
    };
  }

  private removeIrrelevantLocksAndIgnores(relevantRules: Set<number>) {
    for (const ruleId of this.lockedRuleIds) {
      if (!relevantRules.has(ruleId)) this.lockedRuleIds.delete(ruleId);
    }
    for (const ruleId of this.ignoredRuleIds) {
      // Feature state reset managed inside parseAndApply
      if (!relevantRules.has(ruleId)) this.ignoredRuleIds.delete(ruleId);
    }
    this.updateRemoteManagedState({
      lockedRuleIds: this.lockedRuleIds,
      ignoredRuleIds: this.ignoredRuleIds
    });
  }

  private isSelectionAllowedForAssignment(options: MapSelectionOptions, ruleId: RuleId | null) {
    return isSelectionAllowedByOptions(options, !!ruleId) && !this.isLockedOrIgnored(ruleId);
  }

  private isLockedOrIgnored(ruleId: RuleId | null) {
    if (!ruleId) return false;
    return this.lockedRuleIds.has(ruleId) || this.ignoredRuleIds.has(ruleId);
  }

  private updateAccountSelectionFeatureState(accountIds: number[], isSelected: boolean) {
    if (accountIds.length === 0) return;
    this.postMessage({
      type: 'set-accounts-is-selected',
      ids: accountIds,
      isSelected
    });
  }
  private updateGeoSelectionFeatureState(featureIds: FeatureStateIds[], isSelected: boolean) {
    if (featureIds.length === 0) return;

    this.postMessage({
      type: 'set-feature-state',
      bulk: { ids: featureIds, state: { isSelected } }
    });
  }

  private withLoading(
    loadingStateKey: 'isLoadingGeoHierarchy' | 'isLoadingRules' | 'isLoadingSelection' | 'isLoadingCustomerPins',
    callback: () => void
  ) {
    this.updateRemoteManagedState({ [loadingStateKey]: true });
    try {
      callback();
    } finally {
      this.updateRemoteManagedState({ [loadingStateKey]: false });
    }
  }

  private updateRemoteManagedState(statePatch: Partial<WorkerManagedState>) {
    this.postMessage({ type: 'set-worker-managed-state', state: statePatch });
  }
}

export type RuleId = number;
export type GeoId = number;
export type AccountId = number;
export type CustomId = number;

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return `Unknown error: ${error}`;
};
